import React, { useMemo, useRef, useState } from 'react';
import { ServiceAssignment, ServiceItem, ServiceTransaction } from '../types';
import { ArrowLeft, CheckCircle2, History, ListChecks, Pencil, Plus, Receipt, UserRound } from 'lucide-react';
import { Button } from './Button';
import { isNepService, nepReasonLabel, NEP_BADGE, NEP_HINT } from '../services/nepPosition';
import { transferOriginLabel } from '../services/projectLv';
import { ASSIGNMENT_CHOICES, assignmentChoiceLabel, canChangeAssignment, currentAssignment } from '../services/assignmentChange';
import { buildServiceDetail, maskAmounts, postingLabel, ServiceDetailEntry } from './project-hub/serviceDetailLogic';
import { hatBestandteile } from '../services/bestandteile/bestandteilLogic';
import { BestandteileListe, type BestandteileKontext } from './bestandteile/BestandteileDialog';
import {
    dialogRechte,
    istTochterBericht,
    kannBestandteileBekommen,
    plaketteFuer,
    PLAKETTEN_KLASSE,
    stundenPlaketteFuer,
    type OffenUeberschreibung,
} from './bestandteile/bestandteileDialogLogic';

interface Props {
    service: ServiceItem;
    serviceLog: ReadonlyArray<ServiceTransaction>;
    /** Hinweis aus flatCoverageHints, wenn die Monatspauschale diese Position deckt. */
    flatHint?: string;
    canSeeFinancials: boolean;
    isReadOnly: boolean;
    onBack: () => void;
    onTrack: (service: ServiceItem) => void;
    /**
     * Funktion der Position umstellen (assignmentChange). Nur gesetzt, wenn der
     * Betrachter das darf (Admin, kein Lesemodus, kein Tochterprojekt) — sonst
     * steht die Funktion nur als Text da.
     */
    onChangeAssignment?: (service: ServiceItem, next: ServiceAssignment) => void;
    /** Funktionen, die die Deckung einer Monatspauschale verschoeben — ausgegraut. */
    blockedAssignments?: ReadonlySet<ServiceAssignment>;
    /**
     * PAKETPOSITION: der Abschnitt „Bestandteile" mit der Liste aus dem
     * Bestandteile-Dialog (Erledigen, Zuruecknehmen, Verlauf; Admin: festlegen).
     * Ohne Kontext steht dort nur die Plakette und — mit onOpenBestandteile —
     * der Knopf zum Dialog.
     */
    bestandteile?: BestandteileKontext;
    onOpenBestandteile?: (service: ServiceItem) => void;
    /** Lokale Ueberschreibung des Stands aus dem Hub (nur Anzeige der Plakette). */
    bestandteileOffen?: OffenUeberschreibung;
}

const NO_BLOCKED: ReadonlySet<ServiceAssignment> = new Set();

const currency = (val: number) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(val);
const qty = (val: number) => val.toLocaleString('de-DE', { maximumFractionDigits: 2 });
const dateTime = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const postingTone: Record<string, string> = {
    Abrechnung: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    Verbuchung: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    Storno: 'bg-red-50 text-red-700 border-red-200',
    Verzicht: 'bg-amber-50 text-amber-800 border-amber-200',
    Rücknahme: 'bg-slate-100 text-slate-700 border-slate-300',
};

/**
 * Detailansicht EINER Position im Dialog „Leistungen Details": Kennzahlen,
 * jede erbrachte Leistung mit Datum, Bericht und Erbringer, fuer den
 * Innendienst dazu die Abrechnungsvorgaenge.
 *
 * Preise und Abrechnungszeilen nur mit `canSeeFinancials` — derselbe Schnitt
 * wie in der Uebersicht daneben (Aussendienst sieht Mengen, Innendienst Werte).
 */
export const ServiceDetailView: React.FC<Props> = ({ service, serviceLog, flatHint, canSeeFinancials, isReadOnly, onBack, onTrack, onChangeAssignment, blockedAssignments = NO_BLOCKED, bestandteile, onOpenBestandteile, bestandteileOffen }) => {
    const detail = useMemo(() => buildServiceDetail(service, serviceLog, !!flatHint), [service, serviceLog, flatHint]);
    // ── PAKETPOSITION (docs/positionsbestandteile-plan.md, B3/B4) ──────────
    // Pakete bucht nur der Bestandteile-Weg (Kernregel 7): statt „Leistung
    // erfassen" steht „Bestandteil erledigen" da, und der Abschnitt
    // „Bestandteile" zeigt die Liste. Ein Admin kann einer passenden Position
    // (Menge 1, keine Pauschale) hier nachtraeglich Bestandteile geben.
    const paket = hatBestandteile(service);
    const bestandteilAbschnitt = useRef<HTMLElement>(null);
    const [festlegenOffen, setFestlegenOffen] = useState(false);
    const paketSchreibbar = bestandteile
        ? dialogRechte({
            isReadOnly: bestandteile.isReadOnly,
            istFreelancer: !!bestandteile.istFreelancer,
            istTochter: istTochterBericht(bestandteile.report),
            ohneProjekt: !bestandteile.projectId || bestandteile.projectId === 'temp-id',
        }).schreibbar
        : !isReadOnly && !!onOpenBestandteile;
    const darfFestlegen = !paket && !!bestandteile && bestandteile.isAdmin && paketSchreibbar && kannBestandteileBekommen(service);
    const plakette = paket ? plaketteFuer(service, bestandteileOffen) : null;
    // Stunden gegen das Kontingent (intern, S5) — fehlt ohne Kontingent und ohne erfasste Stunden.
    const stundenPlakette = paket ? stundenPlaketteFuer(service, bestandteileOffen) : null;
    const zeigeBestandteile = paket || (darfFestlegen && festlegenOffen);
    const bestandteilErledigen = () => {
        if (bestandteile) bestandteilAbschnitt.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else onOpenBestandteile?.(service);
    };
    const { summary } = detail;
    const price = service.unitPrice || 0;
    const unit = service.unit;
    const origin = transferOriginLabel(service);
    const assignmentEditable = !!onChangeAssignment && canChangeAssignment(service);

    const tiles: Array<{ label: string; value: number; hint?: string; tone?: string }> = [
        { label: 'Beauftragt', value: summary.contracted, hint: isNepService(service) ? 'NEP: zählt erst mit der Buchung' : undefined },
        { label: 'Erbracht', value: summary.used },
    ];
    if (canSeeFinancials) {
        tiles.push({ label: 'Abgerechnet', value: summary.billed });
        if (summary.waived > 0) tiles.push({ label: 'Verzichtet', value: summary.waived, tone: 'text-amber-700' });
        tiles.push({ label: 'Offen', value: summary.open, hint: flatHint ? 'durch Monatspauschale gedeckt' : undefined, tone: summary.open > 0 ? 'text-blue-700' : undefined });
    } else {
        tiles.push({ label: 'Rest bis Soll', value: summary.remaining });
    }

    // Freitext kann Preise tragen — der Aussendienst sieht ihn maskiert.
    const text = (value: string) => (canSeeFinancials ? value : maskAmounts(value));

    const renderWork = (entry: ServiceDetailEntry) => {
        const nepLabel = nepReasonLabel(entry.nepReason);
        const nep = nepLabel ? text(nepLabel) : null;
        return (
            <div key={entry.id} className="bg-white border border-slate-200 rounded-lg p-3 flex gap-3 justify-between">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span className="font-bold text-slate-700">{dateTime(entry.date)}</span>
                        {entry.reportNumber !== null && <span className="font-bold text-blue-700 bg-blue-50 border border-blue-100 px-1.5 rounded">Bericht Nr. {entry.reportNumber}</span>}
                        {entry.isSubstitution && <span className="font-bold text-teal-700 bg-teal-50 border border-teal-200 px-1.5 rounded">Vertretung</span>}
                    </div>
                    {entry.performerName && (
                        <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1"><UserRound className="w-3 h-3" />{entry.performerName}</div>
                    )}
                    {entry.description && <p className="text-xs text-slate-600 mt-1 whitespace-pre-line break-words">{text(entry.description)}</p>}
                    {nep && <p className="text-[11px] text-amber-700 mt-0.5 font-medium break-words">{nep}</p>}
                </div>
                <div className="text-right shrink-0">
                    <div className="font-mono font-bold text-blue-600 text-sm">{qty(entry.quantity)} {unit}</div>
                    {canSeeFinancials && price > 0 && <div className="text-[11px] font-mono text-slate-400">{currency(entry.quantity * price)}</div>}
                </div>
            </div>
        );
    };

    const renderPosting = (entry: ServiceDetailEntry) => {
        const label = postingLabel(entry);
        return (
            <div key={entry.id} className="bg-white border border-slate-200 rounded-lg p-3 flex gap-3 justify-between">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span className={`font-bold border px-1.5 rounded ${postingTone[label] || postingTone.Abrechnung}`}>{label}</span>
                        {entry.reference && <span className="font-mono font-bold text-slate-700">{entry.reference}</span>}
                        <span className="text-slate-500">{dateTime(entry.date)}</span>
                    </div>
                    {(entry.bookedBy || entry.performerName) && (
                        <div className="text-[11px] text-slate-500 mt-0.5">von {entry.bookedBy || entry.performerName}</div>
                    )}
                    {entry.description && <p className="text-xs text-slate-600 mt-1 whitespace-pre-line break-words">{entry.description}</p>}
                </div>
                <div className="text-right shrink-0">
                    <div className="font-mono font-bold text-slate-700 text-sm">{qty(entry.quantity)} {unit}</div>
                    {price > 0 && entry.quantity !== 0 && <div className="text-[11px] font-mono text-slate-400">{currency(entry.quantity * price)}</div>}
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-4">
            <button onClick={onBack} className="text-xs font-bold text-slate-500 hover:text-blue-700 flex items-center gap-1 transition-colors">
                <ArrowLeft className="w-4 h-4" /> Alle Positionen
            </button>

            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="font-mono text-xs font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">{service.lvPosition}</span>
                    {service.clientLvPosition && <span className="font-mono text-xs text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded">Kunde: {service.clientLvPosition}</span>}
                    {isNepService(service) && <span className="text-[11px] font-black text-amber-800 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded" title={NEP_HINT}>{NEP_BADGE}</span>}
                </div>
                <h4 className="font-bold text-slate-800 text-base mb-2 break-words">{service.title}</h4>
                <div className="flex flex-wrap gap-2 mb-2">
                    {origin && <span className="text-[11px] font-medium text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">{origin}</span>}
                    {flatHint && <span className="text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-1.5 py-0.5">{flatHint}</span>}
                </div>
                <div className="flex flex-wrap gap-4 text-xs text-slate-600">
                    <div><span className="text-slate-400 uppercase font-bold text-[11px]">Einheit:</span> {unit}</div>
                    {canSeeFinancials && <div><span className="text-slate-400 uppercase font-bold text-[11px]">Einzelpreis:</span> {currency(price)}</div>}
                    {assignmentEditable ? (
                        <label className="flex items-center gap-1.5">
                            <span className="text-slate-400 uppercase font-bold text-[11px]">Funktion:</span>
                            <select
                                id={`funktion-${service.id}`}
                                value={currentAssignment(service)}
                                onChange={(e) => onChangeAssignment!(service, e.target.value as ServiceAssignment)}
                                className="text-xs border border-slate-200 rounded px-1.5 py-0.5 bg-white text-indigo-700 font-bold focus:ring-2 focus:ring-indigo-500 outline-none"
                            >
                                {ASSIGNMENT_CHOICES.map(c => (
                                    <option key={c.id} value={c.id} disabled={blockedAssignments.has(c.id)}>
                                        {blockedAssignments.has(c.id) ? `${c.label} (ändert Monatspauschale)` : c.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    ) : (
                        <div><span className="text-slate-400 uppercase font-bold text-[11px]">Funktion:</span> {assignmentChoiceLabel(service.assignment)}</div>
                    )}
                </div>
                {assignmentEditable && (
                    <p className="text-[11px] text-slate-400 mt-2">
                        Die Funktion bestimmt, wo die Position zur Buchung angeboten wird (z. B. Begehung, Vorankündigung, Zusatzleistung beim Versand). Bisherige Buchungen bleiben unverändert.
                        {blockedAssignments.size > 0 && ' Ausgegraute Funktionen würden ändern, was die Monatspauschale abdeckt, und sind hier gesperrt.'}
                    </p>
                )}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {tiles.map(t => (
                    <div key={t.label} className="bg-white rounded-xl border border-slate-200 p-3">
                        <div className="text-[11px] font-bold text-slate-400 uppercase">{t.label}</div>
                        <div className={`text-lg font-black ${t.tone || 'text-slate-800'}`}>{qty(t.value)} <span className="text-xs font-bold text-slate-400">{unit}</span></div>
                        {canSeeFinancials && price > 0 && <div className="text-[11px] font-mono text-slate-500">{currency(t.value * price)}</div>}
                        {t.hint && <div className="text-[10px] text-slate-400 mt-0.5">{t.hint}</div>}
                    </div>
                ))}
            </div>

            {zeigeBestandteile && (
                <section ref={bestandteilAbschnitt} className="scroll-mt-4">
                    <h5 className="text-xs font-bold text-slate-500 uppercase flex flex-wrap items-center gap-2 mb-2">
                        <ListChecks className="w-4 h-4" /> Bestandteile
                        {plakette && (
                            <span className={`normal-case rounded border px-1.5 text-[11px] font-semibold ${PLAKETTEN_KLASSE[plakette.ton]}`} title={plakette.titel}>{plakette.text}</span>
                        )}
                        {stundenPlakette && (
                            <span className={`normal-case rounded border px-1.5 text-[11px] font-semibold tabular-nums ${PLAKETTEN_KLASSE[stundenPlakette.ton]}`} title={stundenPlakette.titel}>{stundenPlakette.text}</span>
                        )}
                    </h5>
                    {bestandteile ? (
                        // Dieselbe Instanz auch nach dem ersten Festlegen (paket wird
                        // wahr): die Meldung „gespeichert" bleibt stehen.
                        <BestandteileListe {...bestandteile} service={service} editorStartOffen={!paket} />
                    ) : (
                        <div className="bg-white rounded-lg border border-slate-200 p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-xs text-slate-600">Diese Position wird erst abrechenbar, wenn alle Bestandteile erledigt sind.</p>
                            {onOpenBestandteile && (
                                <Button variant="secondary" onClick={() => onOpenBestandteile(service)} icon={<ListChecks className="w-4 h-4" />}>Bestandteile ansehen</Button>
                            )}
                        </div>
                    )}
                </section>
            )}
            {darfFestlegen && !festlegenOffen && (
                <button
                    type="button"
                    onClick={() => setFestlegenOffen(true)}
                    className="text-xs font-semibold text-slate-600 hover:text-blue-700 flex items-center gap-1.5 min-h-11 sm:min-h-0"
                    title="Die Position wird dann erst abrechenbar, wenn alle Bestandteile erledigt sind (nur intern)."
                >
                    <Pencil className="w-3.5 h-3.5" /> Bestandteile festlegen
                </button>
            )}

            {canSeeFinancials && summary.waived > 0 && service.waivedReason && (
                <div className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <span className="font-bold">Verzicht:</span> {service.waivedReason}
                    {(service.waivedAt || service.waivedBy) && (
                        <span className="text-amber-700"> · {service.waivedAt ? dateTime(service.waivedAt) : ''}{service.waivedBy ? ` von ${service.waivedBy}` : ''}</span>
                    )}
                </div>
            )}

            <section>
                <h5 className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2 mb-2">
                    <History className="w-4 h-4" /> Erbrachte Leistungen
                    <span className="font-mono normal-case text-slate-400">({detail.work.length} Einträge · {qty(detail.workTotal)} {unit})</span>
                </h5>
                {detail.work.length === 0
                    ? <div className="text-center py-6 text-slate-400 italic text-xs bg-white rounded-lg border border-slate-200">Noch keine Leistung erfasst.</div>
                    : <div className="space-y-2">{detail.work.map(renderWork)}</div>}
            </section>

            {canSeeFinancials && (
                <section>
                    <h5 className="text-xs font-bold text-slate-500 uppercase flex items-center gap-2 mb-2">
                        <Receipt className="w-4 h-4" /> Abrechnung & Buchungen
                        <span className="font-mono normal-case text-slate-400">({detail.postings.length})</span>
                    </h5>
                    {detail.postings.length === 0
                        ? <div className="text-center py-6 text-slate-400 italic text-xs bg-white rounded-lg border border-slate-200">Keine Abrechnungsvorgänge zu dieser Position.</div>
                        : <div className="space-y-2">{detail.postings.map(renderPosting)}</div>}
                </section>
            )}

            {/* Paket: kein „Leistung erfassen" — gebucht wird ueber die Bestandteile. */}
            {!paket && !isReadOnly && (
                <Button onClick={() => onTrack(service)} icon={<Plus className="w-4 h-4" />}>Leistung erfassen</Button>
            )}
            {paket && paketSchreibbar && (
                <Button onClick={bestandteilErledigen} icon={<CheckCircle2 className="w-4 h-4" />}>Bestandteil erledigen</Button>
            )}
        </div>
    );
};
